import React, { useState, useEffect } from 'react';
import { superAdminAPI, companyAPI } from '../services/api';


function SuperAdminUsers({ companies: companiesProp = [] }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [companies, setCompanies] = useState(companiesProp);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstname: '',
    surname: '',
    role: 'user',
    company_id: null,
    is_active: true,
    password: ''
  });

  useEffect(() => {
    // If companies prop is empty, fetch them
    if (companies.length === 0) {
      fetchCompanies();
    } else {
      setCompanies(companiesProp);
    }
  }, [companiesProp]);

  useEffect(() => {
    fetchUsers();
  }, [selectedCompanyId]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, activeFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAllCompanies();
      setCompanies(response.data.companies || []);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCompanyId) {
        params.company_id = selectedCompanyId;
      }
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }
      if (activeFilter !== 'all') {
        params.is_active = activeFilter === 'active';
      }
      
      const response = await superAdminAPI.getAllUsers(params);
      setUsers(response.data.users || []);
      setError('');
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        (user.username && user.username.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term)) ||
        (user.firstname && user.firstname.toLowerCase().includes(term)) ||
        (user.surname && user.surname.toLowerCase().includes(term))
      );
    }
    
    setFilteredUsers(filtered);
  };

  const handleEdit = (user) => {
    setCurrentUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      firstname: user.firstname || '',
      surname: user.surname || '',
      role: user.role || 'user',
      company_id: user.company_id || null,
      is_active: user.is_active !== false,
      password: ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await superAdminAPI.updateUser(currentUser.id, formData);
      setSuccess('User updated successfully!');
      setShowEditModal(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Permanently delete this user and related data? This action cannot be undone.')) {
      return;
    }
    
    try {
      await superAdminAPI.deleteUser(userId);
      setSuccess('User permanently deleted successfully!');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await superAdminAPI.updateUser(user.id, { is_active: !user.is_active });
      setSuccess(`User ${!user.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return 'N/A';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company ${companyId}`;
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="admin-section">
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="section-header">
        <h2>All Users</h2>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px'}}>
          <select
            value={selectedCompanyId || ''}
            onChange={(e) => setSelectedCompanyId(e.target.value ? parseInt(e.target.value) : null)}
            style={{padding: '5px'}}
          >
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
          
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{padding: '5px'}}
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="company_admin">Company Admin</option>
            <option value="user">User</option>
          </select>
          
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            style={{padding: '5px'}}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{padding: '5px', minWidth: '200px'}}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Company</th>
              <th>Status</th>
              <th>Bikes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="9" style={{textAlign: 'center'}}>No users found</td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username || 'N/A'}</td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{user.firstname && user.surname ? `${user.firstname} ${user.surname}` : 'N/A'}</td>
                  <td>
                    <span className={`badge ${user.role === 'super_admin' ? 'badge-danger' : user.role === 'company_admin' ? 'badge-warning' : 'badge-info'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{getCompanyName(user.company_id)}</td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{user.bike_count || 0}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(user)}
                      className="btn btn-sm btn-info"
                      style={{marginRight: '5px'}}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`btn btn-sm ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                      style={{marginRight: '5px'}}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    {user.role !== 'super_admin' && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete Permanently
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUpdate} style={{ padding: '24px' }}>
              <div className="form-group">
                <label>Username:</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>First Name:</label>
                  <input
                    type="text"
                    value={formData.firstname}
                    onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Surname:</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Role:</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    required
                  >
                    <option value="user">User</option>
                    <option value="technician">Technician</option>
                    <option value="company_admin">Company Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Company:</label>
                  <select
                    value={formData.company_id || ''}
                    onChange={(e) => setFormData({...formData, company_id: e.target.value ? parseInt(e.target.value) : null})}
                  >
                    <option value="">No Company</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>New Password (optional):</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Leave blank to keep current"
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  style={{ width: 'auto' }}
                />
                <label htmlFor="is_active" style={{ margin: 0 }}>Active</label>
              </div>
              <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Update</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminUsers;
